import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { Space } from 'antd';
import { PageHeader } from './PageHeader';
import { SectionCard } from './SectionCard';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

interface FeaturePreview {
  label: string;
  description?: string;
}

interface ComingSoonPageProps {
  accent: 'ink' | 'violet' | 'green' | 'amber';
  icon: ReactNode;
  title: string;
  subtitle?: string;
  description: string;
  /** When this surface ships, e.g. "Sprint 3" or "H2" */
  eta?: string;
  /** Bullet points showing what the surface will do */
  features: FeaturePreview[];
  /** Optional admin notice */
  adminOnly?: boolean;
  /** Optional help-drawer topic key (renders a (?) hotspot next to the title) */
  helpTopic?: string;
}

// Reusable placeholder for routes that have a clear product surface but
// aren't fully wired yet. Communicates the product vision concretely so
// the page never feels empty / unfinished.

export const ComingSoonPage = observer(function ComingSoonPage({
  accent,
  icon,
  title,
  subtitle,
  description,
  eta,
  features,
  adminOnly,
  helpTopic,
}: ComingSoonPageProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const accentBg: Record<typeof accent, string> = {
    ink: isDark ? `${colors.ink[700]}33` : colors.ink[50],
    violet: isDark ? `${colors.violet[700]}33` : colors.violet[50],
    green: isDark ? `${colors.green[700]}33` : colors.green[100],
    amber: isDark ? `${colors.amber[700]}33` : colors.amber[100],
  };
  const accentColor: Record<typeof accent, string> = {
    ink: colors.ink[600],
    violet: colors.violet[600],
    green: colors.green[600],
    amber: colors.amber[600],
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent={accent}
        icon={icon}
        title={
          helpTopic ? (
            <Space size={6} align="center">
              <span>{title}</span>
              <HelpHotspot topic={helpTopic} />
            </Space>
          ) : (
            title
          )
        }
        subtitle={subtitle}
      />

      <div
        style={{
          padding: '28px 28px 24px',
          borderRadius: 16,
          background: isDark
            ? `linear-gradient(135deg, ${accentBg[accent]} 0%, ${colors.slate[800]} 100%)`
            : `linear-gradient(135deg, ${accentBg[accent]} 0%, #FFFFFF 100%)`,
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accentColor[accent]}22 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: accentColor[accent],
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
              boxShadow: `0 6px 16px ${accentColor[accent]}40`,
            }}
          >
            {icon}
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {eta && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 9px',
                    borderRadius: 999,
                    background: accentColor[accent],
                    color: '#FFFFFF',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Ships in {eta}
                </span>
              )}
              {adminOnly && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: 4,
                    background: isDark ? `${colors.violet[700]}60` : colors.violet[100],
                    color: colors.violet[600],
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Admin
                </span>
              )}
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.65, color: isDark ? colors.slate[200] : colors.slate[700], maxWidth: 720 }}>
              {description}
            </div>
          </div>
        </div>
      </div>

      <SectionCard
        title="What this surface will do"
        subtitle="The capabilities planned for this page. Data layer is wired in the backend; UI is queued."
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {features.map((f) => (
            <div
              key={f.label}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                background: isDark ? colors.slate[900] : colors.slate[50],
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 10,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  background: accentColor[accent],
                  color: '#FFFFFF',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                <CheckOutlined />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: isDark ? colors.slate[100] : colors.slate[900] }}>
                  {f.label}
                </div>
                {f.description && (
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5, display: 'block', marginTop: 2 }}>
                    {f.description}
                  </Text>
                )}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
});
